/**
 * The native-player review's additions to the shared ChatThread: seekable
 * moment pills, the current cut, the session-owned input ref (so staging a
 * moment can focus it), marker → message scrolling, the rich composer
 * (tools row, staged moment and files, and their send), the content thread's
 * files for thumbnails and the viewer, the viewer's open state (the
 * review shortcuts stand down while it's up), the note-card layout and the
 * notes composer's placeholder.
 *
 * Every other surface gets `undefined` and renders the chat exactly as before.
 */
import React from 'react';

import { useThreadFile } from '@discovr/features';
import type { MessageAttachment } from '@discovr/types';

import { CHAT_LAYOUT_CARDS, type ChatThreadProps, type FileRequestOptions, type FileSource, type FileSourceResult, type RichComposer } from '../ChatThread';
import { MomentTools, type MomentToolsSession, type NotesSession } from './notes';
import { NOTES_COMPOSER_PLACEHOLDER } from './notes/lib/composerPlaceholder';
import type { ContentNotesMode } from './useAnchoredNotesPanel';

const EMPTY_DRAFT = '';
/** No notes mode: the file source is built but never handed to the chat. */
const NO_ENTRY_ID = '';

/** The ChatThread props only the native-player review adds. */
export type NotesChatProps = Pick<
  ChatThreadProps,
  | 'onSeekToMoment'
  | 'currentContentVersionId'
  | 'textareaRef'
  | 'scrollToMessageId'
  | 'richComposer'
  | 'fileSource'
  | 'onViewerOpenChange'
  | 'layout'
  | 'composerPlaceholder'
>;

/** Only someone who can send on an open thread may attach — the same gate as the moment tools. */
function filePickerFor(session: Pick<NotesSession, 'canWrite' | 'isLocked' | 'filePicker'>): RichComposer['filePicker'] {
  if (!session.canWrite || session.isLocked) return undefined;
  return session.filePicker;
}

/**
 * The composer extension, memoised on exactly what it and its tools row read —
 * the session object itself is new every render, so keying on it would never hold.
 */
function useRichComposer(session: NotesSession): RichComposer {
  const { canWrite, isLocked, canCapture, canCaptureNow, staged, files, uploadProgress, requestCapture, addTimestamp, clearStaged, filePicker, removeFile, sendRich } =
    session;
  const ready = session.controller.ready;

  return React.useMemo(() => {
    const toolsSession: MomentToolsSession = { canWrite, isLocked, canCapture, canCaptureNow, staged, requestCapture, addTimestamp, controller: { ready } };
    return {
      staged,
      files,
      uploadProgress,
      tools: <MomentTools session={toolsSession} />,
      onClear: clearStaged,
      filePicker: filePickerFor({ canWrite, isLocked, filePicker }),
      onRemoveFile: removeFile,
      onSend: sendRich,
    };
  }, [canWrite, isLocked, canCapture, canCaptureNow, staged, files, uploadProgress, requestCapture, addTimestamp, ready, clearStaged, filePicker, removeFile, sendRich]);
}

function attachmentDownloadName(attachment: MessageAttachment): string {
  return attachment.name;
}

/**
 * The content thread's files, by id, for the chat's thumbnails and viewer.
 * `useFile` is a hook: the chat calls it once, unconditionally, at the top of
 * each small component that shows a file — so another entry (and with it a
 * new `useFile`) keeps every call site's hook order.
 */
function useContentFileSource(entryId: string): FileSource {
  return React.useMemo<FileSource>(() => {
    function useFile(fileId: string, options?: FileRequestOptions): FileSourceResult {
      return useThreadFile(entryId, fileId, options);
    }
    return { useFile, downloadName: attachmentDownloadName };
  }, [entryId]);
}

export function useNotesChatProps(session: NotesSession, notes: ContentNotesMode | undefined): NotesChatProps | undefined {
  const richComposer = useRichComposer(session);
  const fileSource = useContentFileSource(notes?.entryId ?? NO_ENTRY_ID);
  const { seekTo, textareaRef, focusMessage, setFileViewerOpen } = session;
  const enabled = !!notes;
  const versionId = notes?.versionId;

  return React.useMemo(() => {
    if (!enabled) return undefined;
    return {
      onSeekToMoment: seekTo,
      currentContentVersionId: versionId,
      textareaRef,
      scrollToMessageId: focusMessage,
      richComposer,
      fileSource,
      onViewerOpenChange: setFileViewerOpen,
      layout: CHAT_LAYOUT_CARDS,
      composerPlaceholder: NOTES_COMPOSER_PLACEHOLDER,
    };
  }, [enabled, versionId, seekTo, textareaRef, focusMessage, richComposer, fileSource, setFileViewerOpen]);
}

/**
 * The chat's draft, held above the chat so it outlives the chat's remount when
 * the player becomes native — but outside React state, so a keystroke
 * re-renders the chat that shows it and never the panel that holds it.
 */
export interface ThreadDraftStore {
  get(): string;
  set(next: string): void;
  subscribe(onChange: () => void): () => void;
}

function createThreadDraftStore(initial: string): ThreadDraftStore {
  let draft = initial;
  const listeners = new Set<() => void>();
  return {
    get() {
      return draft;
    },
    set(next) {
      if (next === draft) return;
      draft = next;
      listeners.forEach((listener) => listener());
    },
    subscribe(onChange) {
      listeners.add(onChange);
      return () => {
        listeners.delete(onChange);
      };
    },
  };
}

/**
 * The panel's draft store. `seed` (`ContentNotesMode.initialText` — an unsent
 * draft carried over when the player becomes native) fills the input once per
 * distinct value, and never over text the user has typed.
 */
export function useThreadDraftStore(seed: string | undefined): ThreadDraftStore {
  const [store] = React.useState(() => createThreadDraftStore(seed ?? EMPTY_DRAFT));
  const appliedSeedRef = React.useRef(seed);

  React.useEffect(() => {
    if (!seed || seed === appliedSeedRef.current) return;
    appliedSeedRef.current = seed;
    if (store.get()) return;
    store.set(seed);
  }, [seed, store]);

  return store;
}

/** The chat's controlled draft, read from the panel's store by the component that renders the chat. */
export function useThreadDraft(store: ThreadDraftStore): [string, (next: string) => void] {
  const draft = React.useSyncExternalStore(store.subscribe, store.get, store.get);
  return [draft, store.set];
}
